import type { TabState } from '../../types';
import { getFiltered } from '../../utils/filter';
import { fmtG, median, avgArr } from '../../utils/format';
import { RDOTS, SUB_REGIONS } from '../../utils/regions';
import { MetricCard } from '../ui/MetricCard';
import { SectionHeader } from '../ui/SectionHeader';
import { TrendChart } from '../charts/TrendChart';
import { useDeals } from '../../context/DealsContext';
interface Props {
  state: TabState & {
    status: string;
  };
  onDrillRegion: (reg: string, sub: string) => void;
}
export function VPPanel({
  state,
  onDrillRegion
}: Props) {
  const {
    deals
  } = useDeals();
  const all = getFiltered('vp', state, deals);
  const ip = all.filter(d => d.status === 'In Progress');
  const cls = all.filter(d => d.status === 'Closed');
  const onh = all.filter(d => d.status === 'On Hold');
  const can = all.filter(d => d.status === 'Cancelled');
  const scs = new Set(ip.map(d => d.sc)).size;
  const pipelineOppGnarr = all.reduce((s, d) => s + (d.opp_gnarr || 0), 0);
  const ages = ip.map(d => d.age);
  const titleLbl = state.sub && state.sub !== 'All' ? state.sub : state.r;
  const crumb = state.sub && state.sub !== 'All';

  // Region/sub breakdown
  const showSub = state.r !== 'Global' && !!SUB_REGIONS[state.r];
  const regMap: Record<string, {
    ip: number;
    gnarr: number;
    closed: number;
    onhold: number;
    book: number;
    parent: string;
  }> = {};
  all.forEach(d => {
    const key = showSub ? d.sub_region : d.region;
    if (!regMap[key]) regMap[key] = {
      ip: 0,
      gnarr: 0,
      closed: 0,
      onhold: 0,
      book: 0,
      parent: d.region
    };
    regMap[key].book++;
    if (d.status === 'In Progress') {
      regMap[key].ip++;
      regMap[key].gnarr += d.gnarr;
    } else if (d.status === 'Closed') regMap[key].closed++;else if (d.status === 'On Hold') regMap[key].onhold++;
  });
  const maxIP = Math.max(...Object.values(regMap).map(r => r.ip), 1);
  const regRows = Object.entries(regMap).sort((a, b) => b[1].ip - a[1].ip);

  // Book status bar — "In Progress" uses Adobe red
  const total = ip.length + cls.length + onh.length + can.length || 1;
  const segs = [{
    l: 'Closed',
    v: cls.length,
    c: '#34d399'
  }, {
    l: 'In Progress',
    v: ip.length,
    c: '#fa0f00'
  }, {
    l: 'On Hold',
    v: onh.length,
    c: '#d4a017'
  }, {
    l: 'Cancelled',
    v: can.length,
    c: '#f87171'
  }];

  // Close quarter grid
  const cqMap: Record<string, {
    n: number;
    g: number;
  }> = {};
  ip.forEach(d => {
    if (!cqMap[d.close_q]) cqMap[d.close_q] = {
      n: 0,
      g: 0
    };
    cqMap[d.close_q].n++;
    cqMap[d.close_q].g += d.gnarr;
  });
  const cqShow = Object.keys(cqMap).filter(q => q.includes('2026') || q === '2027-Q1').sort().slice(0, 3);
  const qColors = ['#d4a017', '#fa0f00', '#34d399'];
  return <div data-id="element-231">
      {/* Metrics */}
      <div className="mrow" data-id="element-232">
        <MetricCard label="In-progress deals" value={ip.length} sub={`${scs} active SCs`} colorClass="c-blue" data-id="element-233" />
        <MetricCard label="Median age" value={`${median(ages)}d`} sub={`mean ${avgArr(ages)}d`} colorClass="c-gold" data-id="element-234" />
        <MetricCard label="Closed this period" value={cls.length} sub={`${fmtG(cls.reduce((s, d) => s + d.gnarr, 0))} GNARR`} colorClass="c-green" data-id="element-235" />
        <MetricCard label="GNARR Influenced" value={fmtG(pipelineOppGnarr)} sub="total opp GNARR (created qtr)" colorClass="c-gold" data-id="element-236" />
      </div>

      {/* Trend chart */}
      <SectionHeader title={<>{titleLbl} — request volume &amp; GNARR by Created Quarter{crumb && <span className="region-crumb" data-id="element-238">{state.sub}</span>}</>} data-id="element-237" />
      <TrendChart region={state.r} sub={state.sub} deals={deals} data-id="element-239" />

      {/* Region breakdown table */}
      <SectionHeader title={<>Business impact by region — {titleLbl}{crumb && <span className="region-crumb" data-id="element-241">{state.sub}</span>}</>} data-id="element-240" />
      <div className="tw" data-id="element-242">
        <div className="sub" data-id="element-243">
          In-progress load, pipeline GNARR, and deals closed. Click a region row to drill into SC Leaders view.
        </div>
        <table className="tbl" data-id="element-244">
          <thead data-id="element-245">
            <tr data-id="element-246">
              <th style={{
              width: '14%'
            }} data-id="element-247">Region</th>
              <th style={{
              width: '26%'
            }} data-id="element-248">In-progress</th>
              <th style={{
              width: '16%'
            }} data-id="element-249">Pipeline GNARR</th>
              <th style={{
              width: '12%'
            }} data-id="element-250">Closed</th>
              <th style={{
              width: '12%'
            }} data-id="element-251">Total book</th>
              <th style={{
              width: '10%'
            }} data-id="element-252">On hold</th>
              <th style={{
              width: '10%'
            }} data-id="element-253">Sub-region</th>
            </tr>
          </thead>
          <tbody data-id="element-254">
            {regRows.map(([reg, d]) => <tr key={reg} onClick={() => onDrillRegion(d.parent || reg, showSub ? reg : '')} data-id="element-255">
                <td data-id="element-256">
                  <span className="rdot" style={{
                background: RDOTS[reg] || '#888'
              }} data-id="element-257" />
                  {reg}
                </td>
                <td data-id="element-258">
                  <div className="pbg" style={{
                width: 90
              }} data-id="element-259">
                    <div className="pf" style={{
                  width: `${Math.round(d.ip / maxIP * 100)}%`
                }} data-id="element-260" />
                  </div>
                  <strong data-id="element-261">{d.ip}</strong>
                </td>
                <td data-id="element-262">{fmtG(d.gnarr)}</td>
                <td style={{
              color: d.closed > 0 ? '#15803D' : '#344054',
              fontWeight: d.closed > 0 ? 600 : 400
            }} data-id="element-263">{d.closed}</td>
                <td data-id="element-264">{d.book}</td>
                <td data-id="element-265">{d.onhold}</td>
                <td data-id="element-266">{showSub ? <span className="badge b-gray" style={{
                fontSize: 8
              }} data-id="element-267">{d.parent}</span> : '—'}</td>
              </tr>)}
          </tbody>
        </table>
      </div>

      {/* Book status + CQ grid */}
      <div className="br2" data-id="element-268">
        <div className="bcard" data-id="element-269">
          <div className="sh" style={{
          padding: '0 0 8px'
        }} data-id="element-270">
            <div className="shb" data-id="element-271" />
            <span className="sht" data-id="element-272">Book status mix</span>
          </div>
          <div className="book-bar" data-id="element-273">
            {segs.map(s => <div key={s.l} style={{
            width: `${Math.round(s.v / total * 100)}%`,
            height: '100%',
            background: s.c
          }} data-id="element-274" />)}
          </div>
          <div className="bleg" data-id="element-275">
            {segs.map(s => <span key={s.l} data-id="element-276">
                <span className="bldot" style={{
              background: s.c
            }} data-id="element-277" />
                {s.l} {s.v}
              </span>)}
          </div>
        </div>
        <div className="bcard" data-id="element-278">
          <div className="sh" style={{
          padding: '0 0 6px'
        }} data-id="element-279">
            <div className="shb" data-id="element-280" />
            <span className="sht" data-id="element-281">In progress by close quarter</span>
          </div>
          <div className="cqg" data-id="element-282">
            {cqShow.map((q, i) => <div key={q} className="cqc" data-id="element-283">
                <div className="cql" data-id="element-284">{q}</div>
                <div className="cqn" data-id="element-285">{cqMap[q].n}</div>
                <div className="cqs" data-id="element-286">in-progress deals</div>
                <div className="cqs" data-id="element-287">{fmtG(cqMap[q].g)} pipeline</div>
                <div className="cqp" style={{
              background: qColors[i] || '#4f9cf9'
            }} data-id="element-288" />
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}